import { ImageRatio } from '@renderer-ui-library/mui/utils/media/ImageRatio';
import { environment } from 'environment/environment';
import { SetRequired } from 'type-fest';
import {
    ImageTransformations,
    imageTransformationsToString,
    isCmsMediaFile,
    Media,
} from './Media';
const { CMS_MEDIA_CDN_URL, EXTERNAL_MEDIA_CDN_URL, APP_MEDIA_CDN_URL } =
    environment;

const sizeSets = {
    small: [150, 300],
    medium: [300, 400, 600],
    large: [300, 400, 600, 900],
    large2: [400, 600, 900, 1200],
    extraLarge: [400, 600, 900, 1200, 1600],
};

type ImgAttributes = React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
>;

export type ImageSizeSet = keyof typeof sizeSets;

export const getMediaUrl = (
    media: Media,
    {
        ratio,
        disableMachineWatermark,
        createVideoThumbnail,
        download,
        ...rest
    }: ImageTransformations & {
        ratio?: 'original' | ImageRatio;
        disableMachineWatermark?: boolean;
        createVideoThumbnail?: boolean;
        download?: boolean;
    } = {},
): string => {
    let baseUrl = '';
    const transformations: ImageTransformations = rest ?? {};

    if (typeof ratio === 'number' && transformations.width) {
        transformations.aspectRatio = `1000-${Math.round(1000 / ratio)}`;
    }

    let videoThumbnail = false;

    if (isCmsMediaFile(media)) {
        baseUrl = `${CMS_MEDIA_CDN_URL}/${media.attributes.hash}${media.attributes.ext}`;
    } else if (media.type === 'machine') {
        if (media.mime.startsWith('video')) {
            baseUrl = `${APP_MEDIA_CDN_URL}${media.url}`;
        } else {
            baseUrl = `${EXTERNAL_MEDIA_CDN_URL}${media.url}`;
        }
        if (!disableMachineWatermark && transformations.width) {
            // get space base on image width in range of [5,10]px
            const space = Math.min(
                10,
                Math.max(5, Math.floor(transformations.width / 100)),
            );
            const wmWidth = Math.floor(transformations.width * 0.15);
            // svg watermarks cannot be scaled, so we have to use a png in this case
            transformations.imageLayer = `image,i-@@website-machine-images-watermarks@@watermark_DZDDMXPYs_M9F2mQxfH.png,lx-${space},ly-${space},w-${wmWidth},l-end`;
        }

        if (createVideoThumbnail && media.mime.startsWith('video')) {
            videoThumbnail = true;
        }
    } else if (media.type === 'zoho-tools') {
        baseUrl = `${environment.ZOHO_TOOLS_FILE_CDN_URL}/${media.s3Ref}`;

        if (createVideoThumbnail && media.mime.startsWith('video')) {
            videoThumbnail = true;
        }
    } else if (media.type === 'local') {
        // local media gets automaticall prefixed with NEXT_PUBLIC_WEBSITE_ASSETS_CDN_URL, but for images we want to use imagekit
        baseUrl = media.url.replace(
            environment.WEBSITE_ASSETS_CDN_URL ?? '',
            environment.WEBSITE_ASSETS_CDN_URL_IMAGEKIT ?? '',
        );
    }

    const transformationString = imageTransformationsToString(transformations);

    const params = new URLSearchParams();
    if (download) {
        params.set('ik-attachment', 'true');
    }
    if (transformationString) {
        params.set('tr', transformationString);
    }

    return `${baseUrl}${videoThumbnail ? '/ik-thumbnail.jpg' : ''}${params.size > 0 ? `?${params.toString()}` : ''}`;
};

type ImgAttributesRequired = SetRequired<ImgAttributes, 'src' | 'srcSet'>;

export const getMediaSrc = (
    media: Media,
    {
        ratio: displayRatio,
        disableMachineWatermark = undefined,
        sizeSet,
        createVideoThumbnail,
    }: {
        ratio?: 'original' | ImageRatio;
        desktopViewportPercentage?: number;
        disableMachineWatermark?: boolean;
        sizeSet?: ImageSizeSet;
        createVideoThumbnail?: boolean;
    },
): ImgAttributesRequired => {
    const sizes = sizeSets[sizeSet ?? 'large'];
    const attributes: ImgAttributesRequired = {
        src: getMediaUrl(media, {
            ratio: displayRatio,
            width: sizes[sizes.length - 1],
            disableMachineWatermark,
            createVideoThumbnail,
        }),
        srcSet: sizes
            .map(size => {
                return `${getMediaUrl(media, {
                    ratio: displayRatio,
                    width: size,
                    disableMachineWatermark,
                    createVideoThumbnail,
                })} ${size}w`;
            })
            .join(', '),
        height: 'auto',
        alt: isCmsMediaFile(media) ? media.attributes.alternativeText : '',
    };
    return attributes;
};
